import { toast } from 'react-toastify';
import { apiService } from '../services/apiService';
import { store } from '../store';
import { validateImageFile as validateImageFileHelper } from './fileValidation';

export type S3FolderCategory = 'pitch-video' | 'profile' | 'gst-certificate' | 'cover-photo';

interface UploadOptions {
  folder?: string;
  onProgress?: (progress: number) => void;
  companyId?: number;
  companyName?: string;
  userId?: number | string;
}

/**
 * Resolves the standardized S3 folder path based on company and user ID structure:
 * company > user_id > folder_category
 *
 * Folder categories:
 * - company/<user_id>/pitch-video
 * - company/<user_id>/profile
 * - company/<user_id>/gst-certificate
 * - company/<user_id>/cover-photo
 */
export function getCompanyS3Folder(
  category: string = 'profile',
  explicitUserId?: number | string
): string {
  let userId = explicitUserId;

  if (!userId) {
    try {
      const state = store.getState() as any;
      userId = state?.auth?.user?.id || state?.auth?.user?.user_id;
    } catch {
      // ignore
    }
  }

  if (!userId) {
    try {
      const userStr = localStorage.getItem('user') || localStorage.getItem('auth_user');
      if (userStr) {
        const parsed = JSON.parse(userStr);
        userId = parsed?.id || parsed?.user_id;
      }
    } catch {
      // ignore
    }
  }

  let cat = (category || 'profile').trim();

  // If already full custom S3 folder path starting with 'company/'
  if (cat.startsWith('company/')) {
    return cat;
  }

  // Normalize category names
  const lowerCat = cat.toLowerCase();
  if (
    lowerCat === 'company-logos' ||
    lowerCat === 'company-logo' ||
    lowerCat === 'profile-pic' ||
    lowerCat === 'profile_pic' ||
    lowerCat === 'avatar' ||
    lowerCat === 'logo' ||
    lowerCat === 'profile'
  ) {
    cat = 'profile';
  } else if (
    lowerCat === 'pitch-videos' ||
    lowerCat === 'pitch-video' ||
    lowerCat === 'pitch-thumbnails' ||
    lowerCat === 'pitch-thumbnail' ||
    lowerCat === 'video'
  ) {
    cat = 'pitch-video';
  } else if (
    lowerCat === 'gst' ||
    lowerCat === 'gst-certificates' ||
    lowerCat === 'gst-certificate' ||
    lowerCat === 'gst_certificate' ||
    lowerCat === 'gst-cert'
  ) {
    cat = 'gst-certificate';
  } else if (
    lowerCat === 'cover' ||
    lowerCat === 'company-cover' ||
    lowerCat === 'cover-photo' ||
    lowerCat === 'cover-photos' ||
    lowerCat === 'cover_photo'
  ) {
    cat = 'cover-photo';
  }

  const userIdSegment = userId ? String(userId) : 'user_id';
  return `company/${userIdSegment}/${cat}`;
}

/**
 * Upload file to AWS S3 via backend API
 * @param file - File to upload
 * @param options - Upload options (folder, progress callback, company info)
 * @returns Promise with the uploaded file URL
 */
export const uploadToS3 = async (
  file: File,
  options: UploadOptions = {}
): Promise<string> => {
  const { folder = 'profile', onProgress, companyId, companyName, userId } = options;
  const s3Folder = getCompanyS3Folder(folder, userId || companyId);

  try {
    // Validate file using central helper
    const validation = validateImageFileHelper(file, { maxSizeMB: 2, label: 'Image' });
    if (!validation.valid) {
      throw new Error(validation.error || 'Invalid file');
    }

    // Upload via backend API with company & folder information
    const result = await apiService.uploadImage(
      file, 
      s3Folder, 
      onProgress,
      { companyId, companyName, userId }
    );
    
    return result.url;
  } catch (error: any) {
    console.error('Upload error:', error);
    
    if (error.message) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Image upload failed";
      toast.error(message);
    } else {
      toast.error('Failed to upload image. Please try again.');
    }
    
    throw error;
  }
};

/**
 * Validate image file before upload
 * @param file - File to validate
 * @returns Validation result
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  return validateImageFileHelper(file, { maxSizeMB: 5, label: 'Image' });
};
